import { DATA } from '@/data'
import { motion } from 'framer-motion'
import { ProjectCard } from '@/components/project-card'
import { Link } from 'react-router-dom'
import ImageReveal from '@/components/ImageReveal'
import SEO from '@/components/SEO'

function Cmd({ command }: { command: string }) {
    return (
        <p className="font-mono text-sm mb-6">
            <span className="text-primary font-bold">$</span>{' '}
            <span className="text-foreground">{command}</span>
        </p>
    )
}

function FadeSection({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay }}
            className={className}
        >
            {children}
        </motion.div>
    )
}

export default function ProjectsPage() {
    return (
        <div className="min-h-screen font-mono py-12">
            <SEO
                title="Projects | Rapheal Kwabena Asomani Gadoh — Software & Hardware Portfolio"
                description="Explore projects by Rapheal Kwabena Asomani Gadoh: Gadoh Autos, Arduino GitHub Pusher, PyWorkflow Framework, react-pyworkflow, and open-source tools."
                canonical="https://projects.raphealgadoh.me/"
            />
            <div className="max-w-6xl mx-auto px-6 space-y-20">

                {/* ── Page header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <h1 className="text-sm text-[#555] mb-2">
                        <span className="text-primary">rapheal@portfolio</span>
                        <span className="text-[#444]">:~$</span>
                        <span className="text-foreground ml-2">ls -la projects/</span>
                    </h1>
                    <p className="text-xs text-[#555]">
                        total {DATA.projects.length} — sorted by date, descending
                    </p>
                </motion.div>

                {/* ── Projects grid ── */}
                <FadeSection>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {DATA.projects.map((project, idx) => (
                            <motion.div
                                key={project.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.06, duration: 0.4 }}
                                className="h-full"
                            >
                                <ProjectCard
                                    href={project.href}
                                    title={project.title}
                                    description={project.description}
                                    dates={project.dates}
                                    iconLists={project.iconLists}
                                    image={project.image}
                                    links={project.links}
                                />
                            </motion.div>
                        ))}
                    </div>
                </FadeSection>

                {/* ── Community contributions ── */}
                <FadeSection>
                    <Cmd command="cat contributions.md" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        {DATA.contributions.map((c, idx) => (
                            <div key={idx} className="term-card text-xs space-y-2">
                                <p className="text-primary font-bold">{c.title}</p>
                                <p className="text-[#888] leading-relaxed">{c.description}</p>
                            </div>
                        ))}
                    </div>
                </FadeSection>

                {/* ── Awards — Classified Achievements ── */}
                <FadeSection>
                    <Cmd command="cat awards.log" />
                    <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
                        {DATA.hackathons.map((award, idx) => (
                            <motion.div
                                key={award.id}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.07, duration: 0.4 }}
                                className="achievement-card group"
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#111]">
                                    <span className="text-[9px] font-mono text-primary/50 tracking-widest">CLASSIFIED · ACHIEVEMENT</span>
                                    <span className="text-[9px] font-mono text-[#333]">{award.dates}</span>
                                </div>

                                <div className="flex gap-3">
                                    {/* Award image */}
                                    <div className="shrink-0">
                                        <ImageReveal
                                            src={award.logo}
                                            alt={award.title}
                                            className="w-10 h-10 object-contain"
                                            wrapperClassName="w-10 h-10"
                                        />
                                    </div>

                                    <div className="flex-1 text-xs">
                                        <p className="text-foreground font-semibold leading-tight mb-0.5">{award.title}</p>
                                        <p className="text-[#555] text-[10px]">{award.location}</p>
                                        <p className="text-[#666] mt-1.5 text-[10px] leading-relaxed">{award.description}</p>

                                        {/* Hover reveal */}
                                        <div className="mt-2 pt-2 border-t border-[#111] flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <span className="text-[9px] font-mono text-primary/70">SCAN COMPLETE</span>
                                            <span className="text-[9px] font-mono text-[#444]">·</span>
                                            <span className="text-[9px] font-mono text-[#555]">Classification: VERIFIED</span>
                                            <span className="text-[9px] font-mono text-[#444]">·</span>
                                            <span className="text-[9px] font-mono text-primary/40">Achievement Unlocked</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </FadeSection>


                {/* ── CTA ── */}
                <FadeSection>
                    <div className="terminal-window max-w-lg">
                        <div className="terminal-titlebar">
                            <span className="terminal-dot bg-[#ff5f57]" />
                            <span className="terminal-dot bg-[#ffbd2e]" />
                            <span className="terminal-dot bg-[#28c840]" />
                            <span className="ml-3 text-[10px] text-[#444]">collaborate.sh</span>
                        </div>
                        <div className="p-6 space-y-4 text-xs">
                            <p className="text-[#555]">// Interested in collaborating?</p>
                            <p className="text-foreground leading-relaxed">
                                I'm open to projects, creative ideas, or opportunities to be part of your vision.
                            </p>
                            <div className="flex gap-3">
                                <Link to="/contact" className="btn-term-accent text-xs">
                                    [ let's work together ]
                                </Link>
                                <a
                                    href={DATA.contact.social.GitHub.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-term text-xs"
                                >
                                    [ view github ]
                                </a>
                            </div>
                        </div>
                    </div>
                </FadeSection>

            </div>
        </div>
    )
}
